import type { Timesheet } from '@clockwise/clients/timesheets';
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { addDays, shortDayName, weekDates } from './timesheet-week';

/** A contiguous date range to pass to the backend's recallRange endpoint. */
export interface DateRange {
  fromDate: string;
  toDate: string;
}

/** Collapse a set of dates into the minimum number of contiguous ranges so
 *  the caller can fire one `recallRange` per range instead of N per-day
 *  calls. Order-independent input; ranges come back ascending. */
export function groupConsecutiveDates(dates: ReadonlySet<string>): DateRange[] {
  const sorted = [...dates].sort();
  const groups: DateRange[] = [];
  let current: DateRange | null = null;
  for (const date of sorted) {
    if (current && addDays(current.toDate, 1) === date) {
      current.toDate = date;
    } else {
      if (current) groups.push(current);
      current = { fromDate: date, toDate: date };
    }
  }
  if (current) groups.push(current);
  return groups;
}

interface DayChoice {
  date: string;
  /** Eligible iff the day has at least one submitted entry the period will
   *  still let go — a locked one does not make the day eligible. */
  eligible: boolean;
  /** Submitted entries still inside the submission window. This is what the
   *  server will actually flip, so it drives the confirm-button count. */
  submittedCount: number;
  /** Submitted entries whose period has closed for recall. Counted apart so
   *  the day can explain itself rather than reading as "nothing submitted". */
  lockedSubmittedCount: number;
  approvedCount: number;
  hasOtherEntries: boolean;
  hasNoEntries: boolean;
}

function describeDayChoices(
  entries: readonly Timesheet[],
  weekStart: string,
  isDateLocked: (date: string, customerId: string | null) => boolean,
): DayChoice[] {
  return weekDates(weekStart).map((date) => {
    let submitted = 0;
    let lockedSubmitted = 0;
    let approved = 0;
    let other = 0;
    for (const e of entries) {
      if (e.date !== date) continue;
      if (e.status === 'submitted') {
        // Recall is judged on the SUBMISSION boundary in every status, which
        // is `isDateLocked`'s default stage — settlement never reopens it.
        // Scoped per entry because the lock is per (date, customer): one
        // customer's cell can be closed while another's is open on the same
        // day, and `recallRange` filters at exactly that grain.
        if (isDateLocked(e.date, e.customerId)) lockedSubmitted += 1;
        else submitted += 1;
      } else if (e.status === 'approved') approved += 1;
      else other += 1;
    }
    return {
      date,
      eligible: submitted > 0,
      submittedCount: submitted,
      lockedSubmittedCount: lockedSubmitted,
      approvedCount: approved,
      hasOtherEntries: other > 0,
      // `lockedSubmitted` counts here: a day holding only period-locked
      // submitted entries is NOT empty, and saying so would send the user
      // looking for a row that is visible on the grid in front of them.
      hasNoEntries: submitted === 0 && lockedSubmitted === 0 && approved === 0 && other === 0,
    };
  });
}

function disabledReason(choice: DayChoice): string {
  if (choice.hasNoEntries) return 'No entries on this day.';
  // Ahead of the approved/none branches: a day whose submitted entries are all
  // period-locked HAS submitted entries, and saying it doesn't would send the
  // user looking for a row that is right there in front of them.
  if (choice.submittedCount === 0 && choice.lockedSubmittedCount > 0) {
    return 'This period is closed — these entries can no longer be recalled.';
  }
  if (choice.submittedCount === 0 && choice.approvedCount > 0 && !choice.hasOtherEntries) {
    return 'Approved entries can’t be recalled.';
  }
  if (choice.submittedCount === 0) {
    return 'No submitted entries to recall on this day.';
  }
  return '';
}

interface Props {
  entries: readonly Timesheet[];
  weekStart: string;
  /** Whether the period containing `date` has closed for `customerId`. Recall
   *  always asks the SUBMISSION boundary, so the stage argument is left at its
   *  default. Without this the picker would offer days the server then
   *  silently skips, since `recallRange` filters locked rows rather than
   *  refusing the whole range. */
  isDateLocked: (date: string, customerId: string | null) => boolean;
  /** Display name segment for the action button ("Recall Diana's selection"). */
  displayName?: string;
  isDelegating: boolean;
  /** Disable the trigger entirely (mutation in flight, no target user, etc.). */
  disabled?: boolean;
  isPending: boolean;
  /** Caller fires one `recallRange` per range and aggregates the result toast. */
  onRecall: (ranges: DateRange[]) => void;
  /** The trigger button — forwarded via Popover's `asChild`. */
  trigger: React.ReactNode;
}

/**
 * Replaces the all-or-nothing "Recall week" action with a per-day picker.
 * Pre-selects every eligible day on open so single-click ≈ the old
 * behavior; users who want a partial recall toggle days off (or skip
 * straight to the days they care about) before confirming.
 *
 * Day eligibility rules:
 *   - eligible iff the day has ≥1 `submitted` entry on any row that its
 *     period will still release (see `isDateLocked`) — a week straddling a
 *     closed and an open period offers only the open days, matching what
 *     `recallRange` will actually flip
 *   - approved-only days disabled with "Approved entries can't be recalled."
 *   - empty / draft-only days disabled with a descriptive tooltip
 *   - mixed days (one submitted + one approved on the same date) ARE
 *     eligible — the backend's `recallRange` only flips submitted rows,
 *     so the approved row stays put
 *
 * The popover collapses the selected dates into the minimum number of
 * contiguous ranges so the caller can fire one HTTP request per range
 * instead of N per-day. Non-contiguous selections (Mon + Wed, skipping
 * a submitted Tue) become two range calls so Tue isn't recalled
 * incidentally.
 */
export function TimesheetRecallPopover({
  entries,
  weekStart,
  isDateLocked,
  displayName,
  isDelegating,
  disabled,
  isPending,
  onRecall,
  trigger,
}: Props) {
  const choices = useMemo(
    () => describeDayChoices(entries, weekStart, isDateLocked),
    [entries, weekStart, isDateLocked],
  );

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Seed selection only on the open transition (false → true), NOT on every
  // change to `entries` while open. A previous version used
  // `useEffect([open, eligibleDates])` which wiped the user's deselections
  // any time a background refetch produced a new `entries` reference
  // (window-focus refetch, polling, sibling-tab mutations). Driving the
  // seed through `onOpenChange` collapses both concerns: state is set
  // at the exact transition and nothing else can reset it.
  const handleOpenChange = (next: boolean) => {
    if (disabled) return;
    if (next) {
      // Compute eligible dates at the moment of opening — fresher than a
      // memoized snapshot from the last render.
      const eligible = choices.filter((c) => c.eligible).map((c) => c.date);
      setSelected(new Set(eligible));
    } else {
      setSelected(new Set());
    }
    setOpen(next);
  };

  const toggle = (date: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  };

  const selectedChoices = choices.filter((c) => selected.has(c.date));
  const totalEntries = selectedChoices.reduce((acc, c) => acc + c.submittedCount, 0);
  const canConfirm = totalEntries > 0 && !isPending;

  const dayList =
    selectedChoices.length === 0
      ? 'no days selected'
      : selectedChoices.map((c) => shortDayName(c.date)).join(', ');

  const confirmLabel =
    totalEntries === 0
      ? 'Recall'
      : `Recall ${totalEntries} entr${totalEntries === 1 ? 'y' : 'ies'}`;

  const handleConfirm = () => {
    const ranges = groupConsecutiveDates(selected);
    if (ranges.length === 0) return;
    onRecall(ranges);
    handleOpenChange(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="end" className="w-[320px]">
        <div className="space-y-3">
          <div className="space-y-1">
            <h4 className="text-sm font-semibold">
              {isDelegating && displayName ? `Recall ${displayName}'s entries` : 'Recall entries'}
            </h4>
            <p className="text-xs text-muted-foreground">
              Pick the days to pull back to draft. Approval is canceled and you’ll resubmit when
              ready.
            </p>
          </div>

          <div className="grid grid-cols-7 gap-1">
            {choices.map((choice) => {
              const day = shortDayName(choice.date);
              const isSelected = selected.has(choice.date);
              const reason = disabledReason(choice);
              const tooltip = reason
                ? `${day}: ${reason}`
                : `${day}: ${choice.submittedCount} submitted${choice.approvedCount > 0 ? ` · ${choice.approvedCount} approved (stays)` : ''}`;
              return (
                <button
                  key={choice.date}
                  type="button"
                  onClick={() => choice.eligible && toggle(choice.date)}
                  disabled={!choice.eligible}
                  title={tooltip}
                  aria-pressed={isSelected}
                  // Stable hook for E2E + integration tests. `data-day` is
                  // the short-day-name ("Mon", "Tue", ...) and stays
                  // independent of the tooltip wording / aria attrs, so
                  // tests that target a specific chip don't break when
                  // copy or a11y labels change.
                  data-testid="recall-day-chip"
                  data-day={day}
                  className={cn(
                    'flex flex-col items-center gap-0.5 rounded-md border px-1.5 py-1.5 text-xs font-medium transition-colors',
                    choice.eligible
                      ? isSelected
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border bg-background text-foreground hover:bg-muted'
                      : 'cursor-not-allowed border-dashed border-border bg-muted/30 text-muted-foreground/50',
                  )}
                >
                  <span className="text-[10px] uppercase tracking-wide">{day.slice(0, 1)}</span>
                  <span className="tabular-nums">{choice.date.slice(8)}</span>
                </button>
              );
            })}
          </div>

          <div className="text-xs text-muted-foreground">
            {totalEntries === 0
              ? 'Select at least one submitted day.'
              : `${totalEntries} entr${totalEntries === 1 ? 'y' : 'ies'} across ${dayList}.`}
          </div>

          <div className="flex items-center justify-end gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button size="sm" variant="destructive" onClick={handleConfirm} disabled={!canConfirm}>
              {isPending ? 'Recalling…' : confirmLabel}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
